import { useMemo, useState, type ReactNode } from 'react';
import type { Deck, Pilot } from '@edh/shared';
import {
  PAIR_JOIN,
  PLACEMENT_GAMES,
  commandersOf,
  deckLabel,
  deckRecord,
  display,
  formatRecord,
  games,
  isRanked,
  streakForDeck,
} from '@edh/shared';
import { Calibration } from '../components/Calibration';
import { MemberRow } from '../components/MemberRow';
import { RatingBar, ratingRange } from '../components/RatingBar';
import { YouCard } from '../components/YouCard';
import { useDeckArt, usePartnerArt } from '../lib/art';
import { navigate } from '../lib/router';
import { useStore } from '../lib/store';

type Mode = 'guildmates' | 'decks';

/**
 * Both ladders. The Guild tab is for administering who is in the guild; this is
 * where you look to see who is winning.
 *
 * Side by side wherever there is width for it, because the two ratings are only
 * interesting against each other — a deck at 1300 means one thing if its usual
 * pilot is at 1100 and something else entirely if they are at 1400. A toggle
 * makes you hold one column in your head while looking at the other.
 *
 * Below that width they stack behind the toggle, which is what a phone gets:
 * two of these rows abreast on a 390px screen would be unreadable.
 */
export function Standings() {
  const { state } = useStore();
  const [mode, setMode] = useState<Mode>('guildmates');
  // Which way the incoming list should travel, so the content follows the thumb
  // rather than appearing from nowhere.
  const [direction, setDirection] = useState<1 | -1>(1);

  function select(next: Mode) {
    if (next === mode) return;
    setDirection(next === 'decks' ? 1 : -1);
    setMode(next);
  }

  const decks = useMemo(
    () => state.decks.filter((d) => !d.retired).sort((a, b) => b.elo - a.elo),
    [state.decks],
  );
  const members = useMemo(
    () => state.pilots.filter((p) => !p.retired).sort((a, b) => b.elo - a.elo),
    [state.pilots],
  );

  return (
    <>
      <YouCard />

      {/* A segmented control rather than two chips: the thumb slides between
          them, so the switch reads as moving along one axis instead of two
          separate buttons taking turns being lit. */}
      <div className="segmented" role="tablist" aria-label="Standings">
        <span
          className="segmented-thumb"
          style={{ transform: `translateX(${mode === 'decks' ? '100%' : '0%'})` }}
        />
        <button
          className="segmented-option"
          role="tab"
          aria-selected={mode === 'guildmates'}
          onClick={() => select('guildmates')}
        >
          Guildmates
        </button>
        <button
          className="segmented-option"
          role="tab"
          aria-selected={mode === 'decks'}
          onClick={() => select('decks')}
        >
          Decks
        </button>
      </div>

      {/* Keyed on the mode so the list remounts and replays its entry, sliding
          in from the side the thumb travelled towards. */}
      <div key={mode} className={`ladder-swap ${direction === 1 ? 'from-right' : 'from-left'}`}>
        {mode === 'guildmates' ? (
          <Ladder
            heading="Guildmates"
            empty="Add the people who play. Everyone starts at 1200."
            items={members}
            renderRow={(item, rank) => (
              <MemberRow key={item.id} pilot={item as Pilot} rank={rank} />
            )}
          />
        ) : (
          <Ladder
            heading="Decks"
            hint="Bars show where each deck sits between the weakest and strongest in the pool."
            empty="Register a commander and it starts finding its level after five games."
            items={decks}
            renderRow={(item, _rank, range) => (
              <DeckRow key={item.id} deck={item as Deck} range={range} />
            )}
          />
        )}
      </div>

      {/* Below the ladders, because it is a fact about the app rather than
          about anybody in it. */}
      <Calibration />
    </>
  );
}

interface Rated {
  id: string;
  name: string;
  elo: number;
  placementGames: number;
}

/**
 * One ladder: the ranked table, then whatever is still in placement.
 *
 * Both columns share this so the two ratings are presented identically. They
 * are different measurements of the same games, and any difference in how they
 * are drawn would read as a difference in what they mean.
 */
function Ladder({
  heading,
  hint,
  empty,
  items,
  renderRow,
}: {
  heading: string;
  /** What the bars under each row are measuring, said once. */
  hint?: ReactNode;
  empty: string;
  items: Rated[];
  renderRow: (item: Rated, rank: number | undefined, range: { min: number; max: number }) => ReactNode;
}) {
  const ranked = items.filter((i) => isRanked(i.placementGames));
  /**
   * Deliberately not in rating order.
   *
   * The list arrives sorted by rating, which for the ranked table is the point
   * and for this one is a leak: with two people placing, whoever is drawn above
   * the other has the better sealed number, and the whole promise of placement
   * is that nobody can read it yet. Ordered by how close each is to having a
   * rating at all, which is the only thing here anyone can see anyway.
   */
  const inPlacement = items
    .filter((i) => !isRanked(i.placementGames))
    .sort((a, b) => b.placementGames - a.placementGames || a.name.localeCompare(b.name));

  // Scaled against the settled ratings only; anything still in placement swings
  // 48 points a game and would otherwise stretch the axis every match.
  const range = ratingRange((ranked.length > 1 ? ranked : items).map((i) => i.elo));

  return (
    <section>
      <div className="section-head" style={{ marginTop: 4 }}>
        <h2>{heading}</h2>
        {/* How many there are, which is what a count beside a heading means.
            It used to count only the ranked ones, so it read "Guildmates 0"
            above a list of two guildmates. */}
        {items.length > 0 && <span className="hint num">{items.length}</span>}
      </div>

      {hint && ranked.length > 0 && (
        <p className="ladder-hint">{hint}</p>
      )}

      {items.length === 0 && (
        <div className="empty">
          <strong>Nothing rated yet</strong>
          {empty}
        </div>
      )}

      {ranked.length > 0 && (
        <div className="rows">{ranked.map((item, i) => renderRow(item, i + 1, range))}</div>
      )}

      {/* Spec 2.5 — a rating off two games is not a rating, so anything still
          finding its level sits in its own section below the table. */}
      {inPlacement.length > 0 && (
        <>
          <div className="section-head">
            <h2>Placement</h2>
            {/* "5 to rank" could be read as five people. */}
            <span className="hint">{PLACEMENT_GAMES} games to rank</span>
          </div>
          <div className="rows">
            {inPlacement.map((item) => renderRow(item, undefined, range))}
          </div>
        </>
      )}
    </section>
  );
}

function DeckRow({ deck, range }: { deck: Deck; range: { min: number; max: number } }) {
  const { state, lastResult } = useStore();
  const art = useDeckArt(deck);
  const partnerArt = usePartnerArt(deck);
  const ranked = isRanked(deck.placementGames);
  const record = deckRecord(state.matches, deck.id);
  const streak = streakForDeck(state.matches, deck.id);

  // If a match just resolved, animate this bar from where it was before it.
  const delta = lastResult?.match.deltas.decks[deck.id];
  const participant = delta !== undefined;

  return (
    <button
      className={`row${participant ? ' is-participant' : ''}`}
      onClick={() => navigate({ name: 'deck', id: deck.id })}
    >
      {/* The ladder's row art gets the pair too — same diagonal, so a partner
          deck is recognisable at a glance in a list of ratings. */}
      {art && (
        <>
          <div className="row-art" style={{ backgroundImage: `url(${art})` }}>
            {deck.partner && partnerArt && (
              <span
                className="row-art-partner"
                style={{ backgroundImage: `url(${partnerArt})` }}
              />
            )}
          </div>
          {/* Outside the art, because the art is dimmed to a third and washed
              with a gradient — anything drawn inside it goes the same way. */}
          {deck.partner && partnerArt && <span className="row-seam" />}
        </>
      )}
      <div className="row-body">
        <div className="row-top">
          {/* The name the table uses, same as everywhere else. This was the
              commander outright, so a deck everybody calls Superfriends was
              listed here and nowhere else as Atraxa, Praetors' Voice. */}
          <span className="row-name">{deckLabel(deck)}</span>
          {ranked ? (
            <span className="row-rating">{display(deck.elo)}</span>
          ) : (
            // Progress, not a rating, in the place a rating would be.
            <span className="pip gold num">
              {deck.placementGames}/{PLACEMENT_GAMES}
            </span>
          )}
        </div>
        {/* The card underneath the table name, only when there is a table name
            above it to need explaining. */}
        {deck.nickname?.trim() && (
          <div className="deck-commander">{commandersOf(deck).join(PAIR_JOIN)}</div>
        )}
        <div className="row-meta">
          {/* Spec 10 wants games played beside every rating: a 1341 off six
              games does not read the same as a 1341 off sixty. It was always
              wins plus losses, but a number you have to add up is not one
              anybody reads. */}
          <span className="row-games num">{games(record)}</span>
          <span>{games(record) === 1 ? 'game' : 'games'}</span>
          <span className="dot">·</span>
          <span>{formatRecord(record)}</span>
          {streak && streak.length >= 3 && (
            <>
              <span className="dot">·</span>
              <span style={{ color: streak.kind === 'win' ? '#57A566' : '#D45B47' }}>
                {streak.length} {streak.kind === 'win' ? 'W' : 'L'}
              </span>
            </>
          )}
        </div>
        {/* Where this deck sits in the pool, from the weakest registered to the
            strongest. Labelled under the heading rather than per row — the
            question got asked every time it went unexplained. */}
        <RatingBar
          elo={deck.elo}
          min={range.min}
          max={range.max}
          colors={deck.colors}
          provisional={!ranked}
          from={delta !== undefined ? deck.elo - delta : undefined}
          placement={
            ranked ? undefined : { played: deck.placementGames, needed: PLACEMENT_GAMES }
          }
        />
      </div>
    </button>
  );
}
